import { useEffect, useState, useMemo } from "react";
import { api, formatTRY, monthLabel, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Building2, TrendingUp, TrendingDown, Calculator, Wallet, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMonth } from "@/context/MonthContext";

export default function Companies() {
  const { user } = useAuth();
  const { month } = useMonth();
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [search, setSearch] = useState("");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const [c, r] = await Promise.all([
      api.get("/companies"),
      api.get("/reports/monthly", { params: { month } }),
    ]);
    setItems(c.data);
    setReport(r.data);
  };
  useEffect(() => { load(); }, [month]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", form);
      toast.success("Firma eklendi");
      setOpen(false);
      setForm({ name: "", description: "" });
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Firmayı silmek istiyor musunuz? Bağlı işlemler de silinecek.")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Firma silindi");
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const rowFor = (cid) => (report?.companies || []).find(r => r.company_id === cid);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return items;
    return items.filter(c =>
      (c.name || "").toLocaleLowerCase("tr").includes(q) ||
      (c.description || "").toLocaleLowerCase("tr").includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[.25em] text-slate-500">Firmalar</div>
          <h1 className="text-3xl font-bold tracking-tight fk-heading">5 Firma Yönetimi</h1>
          <p className="text-sm text-slate-400 mt-1">
            {monthLabel(month)} dönemine ait aylık gelir, gider ve net kâr her firma kartında gösterilir.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <Input data-testid="company-search" value={search} onChange={(e)=>setSearch(e.target.value)}
                   placeholder="Firma ara…"
                   className="w-[220px] pl-9 bg-slate-900/60 border-slate-700" />
          </div>
          {isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-company-btn" className="bg-indigo-600 hover:bg-indigo-500">
                <Plus className="w-4 h-4 mr-1.5"/> Yeni Firma
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#131b2e] border-white/10">
              <DialogHeader><DialogTitle>Yeni Firma</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Firma Adı</Label>
                  <Input required data-testid="company-name-input"
                         value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
                         className="bg-slate-900/60 border-slate-700" />
                </div>
                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Input data-testid="company-desc-input"
                         value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})}
                         className="bg-slate-900/60 border-slate-700" />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-500" data-testid="company-submit">Kaydet</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map((c) => {
          const r = rowFor(c.id);
          const income = Number(r?.income || 0);
          const expense = Number(r?.direct_expense || 0);
          const mgrShare = Number(r?.manager_share || 0);
          const net = Number(r?.net_profit || 0);
          return (
            <div key={c.id} className="fk-card p-5 flex flex-col" data-testid={`company-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-indigo-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-white fk-heading truncate">{c.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest mt-0.5">
                      {monthLabel(month)}
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <Button variant="ghost" size="sm" onClick={()=>remove(c.id)}
                          data-testid={`delete-company-${c.id}`}
                          className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0 shrink-0">
                    <Trash2 className="w-4 h-4"/>
                  </Button>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2.5">
                <div className="rounded-lg p-3 bg-emerald-500/5 border border-emerald-500/20">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-emerald-400/70">
                    <TrendingUp className="w-3 h-3"/> Gelir
                  </div>
                  <div className="fk-mono text-sm font-semibold text-emerald-300 mt-1 truncate">{formatTRY(income)}</div>
                </div>
                <div className="rounded-lg p-3 bg-rose-500/5 border border-rose-500/20">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-rose-400/70">
                    <TrendingDown className="w-3 h-3"/> Direkt Gider
                  </div>
                  <div className="fk-mono text-sm font-semibold text-rose-300 mt-1 truncate">{formatTRY(expense)}</div>
                </div>
                <div className="rounded-lg p-3 bg-amber-500/5 border border-amber-500/20">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-amber-400/70">
                    <Calculator className="w-3 h-3"/> Ortak Pay
                  </div>
                  <div className="fk-mono text-sm font-semibold text-amber-300 mt-1 truncate">{formatTRY(mgrShare)}</div>
                </div>
                <div className={`rounded-lg p-3 border ${net >= 0 ? "bg-indigo-500/5 border-indigo-500/25" : "bg-rose-500/10 border-rose-500/30"}`}>
                  <div className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider ${net >= 0 ? "text-indigo-400/70" : "text-rose-400/70"}`}>
                    <Wallet className="w-3 h-3"/> Net Kâr
                  </div>
                  <div className={`fk-mono text-sm font-semibold mt-1 truncate ${net >= 0 ? "text-indigo-300" : "text-rose-300"}`}>
                    {formatTRY(net)}
                  </div>
                </div>
              </div>

              {c.description && (
                <div className="mt-3 text-xs text-slate-500 truncate border-t border-white/5 pt-2.5">{c.description}</div>
              )}
            </div>
          );
        })}
        {filteredItems.length === 0 && (
          <div className="col-span-full fk-card p-10 text-center text-slate-500">
            {search ? `"${search}" için firma bulunamadı` : (items.length === 0 ? `Henüz firma yok. ${isAdmin ? "Yeni firma ekleyerek başlayın." : ""}` : "")}
          </div>
        )}
      </div>
    </div>
  );
}
